import { API } from './protocol';
import { ICE_SERVERS } from './protocol';
import {
  STUN_PHASE_TIMEOUT_MS,
  TURN_PHASE_TIMEOUT_MS,
} from './p2pConfig';

export { STUN_PHASE_TIMEOUT_MS, TURN_PHASE_TIMEOUT_MS };

function normalizeUrls(urls: string | string[]): string[] {
  return Array.isArray(urls) ? urls : [urls];
}

export function isTurnUrl(url: string): boolean {
  const u = url.trim().toLowerCase();
  return u.startsWith('turn:') || u.startsWith('turns:');
}

/** Reason an ICE server entry was dropped. */
export type IceRejectReason = 'missing-url' | 'turn-missing-credentials';

export interface IceValidationResult {
  /** Entries that are safe to hand to RTCPeerConnection. */
  servers: RTCIceServer[];
  /** Entries that were dropped, with the reason. */
  rejected: { urls: string; reason: IceRejectReason }[];
}

/**
 * Drops unusable ICE entries before they reach RTCPeerConnection:
 *   - an entry without any URL is dead weight;
 *   - a TURN entry needs a URL *and* credentials (username + credential),
 *     otherwise the browser silently never gathers a relay candidate;
 *   - STUN entries need only a URL, so a STUN-only list stays valid.
 */
export function validateIceServers(servers: RTCIceServer[]): IceValidationResult {
  const out: RTCIceServer[] = [];
  const rejected: { urls: string; reason: IceRejectReason }[] = [];
  for (const s of servers) {
    const urls = normalizeUrls(s.urls as string | string[])
      .filter((u) => typeof u === 'string' && u.trim().length > 0);
    if (urls.length === 0) {
      rejected.push({ urls: '', reason: 'missing-url' });
      continue;
    }
    const turnUrls = urls.filter(isTurnUrl);
    const stunUrls = urls.filter((u) => !isTurnUrl(u));
    const hasCredentials =
      typeof s.username === 'string' &&
      s.username.length > 0 &&
      typeof s.credential === 'string' &&
      s.credential.length > 0;
    if (turnUrls.length > 0) {
      if (!hasCredentials) {
        rejected.push({ urls: turnUrls.join(','), reason: 'turn-missing-credentials' });
      } else {
        out.push({ urls: turnUrls, username: s.username, credential: s.credential });
      }
    }
    if (stunUrls.length > 0) {
      out.push({ urls: stunUrls, username: s.username, credential: s.credential });
    }
  }
  return { servers: out, rejected };
}

/** Split /api/ice list into STUN vs TURN (Metered TURN-only in SIGNAL_ICE_JSON is typical). */
export function partitionIceServers(servers: RTCIceServer[]): {
  stun: RTCIceServer[];
  turn: RTCIceServer[];
} {
  const stun: RTCIceServer[] = [];
  const turn: RTCIceServer[] = [];
  for (const s of servers) {
    const urls = normalizeUrls(s.urls as string | string[]);
    const stunUrls = urls.filter((u) => !isTurnUrl(u));
    const turnUrls = urls.filter((u) => isTurnUrl(u));
    if (stunUrls.length) {
      stun.push({ urls: stunUrls, username: s.username, credential: s.credential });
    }
    if (turnUrls.length) {
      turn.push({ urls: turnUrls, username: s.username, credential: s.credential });
    }
  }
  return { stun, turn };
}

/** STUN-only list for phase 1 (from /api/ice, else built-in Google STUN fallback). */
export function stunPhaseServers(fromApi: RTCIceServer[]): RTCIceServer[] {
  const { stun } = partitionIceServers(fromApi);
  const source = stun.length > 0 ? stun : ICE_SERVERS;
  const seen = new Set<string>();
  const out: RTCIceServer[] = [];
  for (const s of source) {
    for (const u of normalizeUrls(s.urls as string | string[])) {
      if (seen.has(u)) continue;
      seen.add(u);
      out.push({ urls: [u] });
    }
  }
  return out;
}

export function fullIceServers(fromApi: RTCIceServer[]): RTCIceServer[] {
  const stun = stunPhaseServers(fromApi);
  const { turn } = partitionIceServers(fromApi);
  return [...stun, ...turn];
}

/** Load ICE servers from the signaling server (authoritative when SIGNAL_ICE_JSON is set). */
export async function fetchIceServers(serverUrl: string): Promise<RTCIceServer[]> {
  let raw: RTCIceServer[] = ICE_SERVERS;
  try {
    const res = await fetch(`${serverUrl}${API.ice}`);
    if (res.ok) {
      const data = (await res.json()) as { iceServers?: RTCIceServer[] };
      if (Array.isArray(data.iceServers) && data.iceServers.length > 0) {
        raw = data.iceServers;
      }
    }
  } catch {
    /* offline */
  }
  const { servers, rejected } = validateIceServers(raw);
  if (rejected.length > 0) {
    console.warn(
      'ICE config: dropped unusable server(s): ' +
        rejected.map((r) => `${r.urls || '(no url)'} [${r.reason}]`).join(', ')
    );
  }
  return servers;
}

/**
 * Phased dial: try direct + STUN first (IPv6 candidates are prioritized separately
 * via IceCandidateForwarder). If that does not link within STUN_PHASE_TIMEOUT_MS,
 * tear down and redial with the full ICE config from /api/ice.
 *
 * Phase 2 always runs — when TURN is configured it exercises STUN+TURN relay
 * candidates, and when only STUN is configured it retries with a longer budget
 * to give IPv6 / LAN direct candidates a second chance to connect with fresh
 * ICE mappings that a single RTCPeerConnection cannot refresh on its own.
 */
export function dialIceSteps(fromApi: RTCIceServer[]): {
  phase: 'stun' | 'turn';
  servers: RTCIceServer[];
  timeoutMs: number;
}[] {
  const { servers: valid, rejected } = validateIceServers(fromApi);
  if (rejected.length > 0) {
    console.warn(
      'ICE config: dropped unusable server(s): ' +
        rejected.map((r) => `${r.urls || '(no url)'} [${r.reason}]`).join(', ')
    );
  }
  const steps: {
    phase: 'stun' | 'turn';
    servers: RTCIceServer[];
    timeoutMs: number;
  }[] = [
    {
      phase: 'stun',
      servers: stunPhaseServers(valid),
      timeoutMs: STUN_PHASE_TIMEOUT_MS,
    },
  ];
  // Phase 2: always attempt with the full validated ICE config. When TURN is
  // present this covers STUN+TURN; when only STUN is configured it is a longer
  // STUN-only retry (fresh peer → fresh candidates).
  steps.push({
    phase: 'turn',
    servers: fullIceServers(valid),
    timeoutMs: TURN_PHASE_TIMEOUT_MS,
  });
  return steps;
}

export function hasTurnConfigured(servers: RTCIceServer[]): boolean {
  return partitionIceServers(validateIceServers(servers).servers).turn.length > 0;
}
